import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { PrecompiledProgression } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faTrash,
  faChartLine,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { useClipboard } from "../../contexts/clipboard";
import { TailSpin } from "react-loader-spinner";
import { ConfigProvider, Empty, Space, Table, Tag, Tooltip } from "antd";
import IconButton from "../../common/IconButton";
import { PagedResults } from "../../types/generics";
import AppSearch from "../../common/AppSearch";
import { debounce } from "lodash";
import "./PrecompiledProgressions.css";

const TAG_COLORS = ["blue", "purple", "cyan", "geekblue", "volcano", "orange"];
const tagColor = (t: string) => TAG_COLORS[t.charCodeAt(0) % TAG_COLORS.length];

export default function PrecompiledProgressionsList() {
  const { showToast } = useToasts();
  const navigate = useNavigate();

  const { copyPrecompiledProgression } = useClipboard();

  const [items, setItems] = useState<PrecompiledProgression[]>();
  const [error, setError] = useState(false);
  const [total, setTotal] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PrecompiledProgression>();
  // FE-M (#97065f8b) — per-row "Copia progressione" button. The row payload
  // from the paginated endpoint may be a summary; we fetch the full progression
  // by id before pushing it into the clipboard so the paste-target form gets
  // the full `weeks` array.
  const [copyingId, setCopyingId] = useState<string | null>(null);

  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
    freeText: string;
  }>({ pageSize: 20, currentPage: 1, freeText: "" });

  const init = useCallback(() => {
    const params = new URLSearchParams();
    params.append("page", `${filters.currentPage}`);
    params.append("size", `${filters.pageSize}`);
    if (filters.freeText) params.append("freeText", filters.freeText);

    Entities.getPrecompiledProgressionsPaginated(params).then(
      ({ data }) => {
        setItems((data as PagedResults<PrecompiledProgression>).items);
        setTotal((data as PagedResults<PrecompiledProgression>).totalItems);
      },
      () => {
        showToast("pp-error", "Impossibile caricare le progressioni precompilate", "", "danger");
        setItems([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setItems(undefined);
    init();
  }, [init]);

  useEffect(() => { reset(); }, [filters, reset]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((value: string) => {
      setFilters((prev) => ({ ...prev, freeText: value, currentPage: 1 }));
    }, 400),
    []
  );

  const handleDeleteClick = useCallback(
    (item: PrecompiledProgression) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedItem(item);
      setDeleteModal(true);
    },
    []
  );

  const handleEditClick = useCallback(
    (item: PrecompiledProgression) => (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/progressioni-precompilate/${item.id}/modifica`);
    },
    [navigate]
  );

  const handleCopyClick = useCallback(
    (item: PrecompiledProgression) => (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!item.id || copyingId) return;
      setCopyingId(item.id);
      Entities.getPrecompiledProgression(item.id).then(
        ({ data }) => {
          copyPrecompiledProgression(data.progression);
          setCopyingId(null);
          showToast(
            "pp-copy-ok",
            "Progressione copiata negli appunti",
            "Apri una progressione in modifica e usa Incolla.",
            "success",
          );
        },
        () => {
          setCopyingId(null);
          showToast("pp-copy-err", "Errore nella copia della progressione", undefined, "danger");
        },
      );
    },
    [copyPrecompiledProgression, copyingId, showToast],
  );

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModal(false);
    setSelectedItem(undefined);
  }, []);

  const handleDelete = useCallback(() => {
    setLoading(true);
    Entities.deletePrecompiledProgression(selectedItem!.id!).then(
      () => {
        setLoading(false);
        handleDeleteModalClose();
        reset();
        showToast("pp-delete-ok", "Progressione eliminata correttamente", undefined, "success");
      },
      () => {
        setLoading(false);
        showToast("pp-delete-err", "Errore nella cancellazione", undefined, "danger");
      }
    );
  }, [selectedItem, showToast, handleDeleteModalClose, reset]);

  const renderEmpty = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <span style={{ color: "var(--app-gray-500)" }}>
          Nessuna progressione precompilata trovata
        </span>
      }
    />
  );

  const renderTable = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">Errore nel caricamento, riprova più tardi.</span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        dataSource={items}
        rowKey="id"
        locale={{ emptyText: renderEmpty() }}
        onRow={(record) => ({
          className: "pp-row-clickable",
          onClick: () => navigate(`/progressioni-precompilate/${record.id}`),
        })}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          total,
          showSizeChanger: true,
          showTotal: (t) => `${t} progressioni`,
          onChange: (page) => setFilters((prev) => ({ ...prev, currentPage: page })),
          onShowSizeChange: (_c, size) =>
            setFilters((prev) => ({ ...prev, pageSize: size, currentPage: 1 })),
        }}
      >
        <Table.Column
          key="label"
          dataIndex="label"
          title="Nome"
          render={(val: string) => (
            <span style={{ fontWeight: 600, color: "var(--app-ink)" }}>{val}</span>
          )}
        />
        <Table.Column
          key="description"
          dataIndex="description"
          title="Descrizione"
          render={(val) =>
            val ? (
              <span
                style={{
                  color: "var(--app-gray-500)",
                  fontSize: "0.85rem",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  maxWidth: 280,
                }}
              >
                {val}
              </span>
            ) : (
              <span style={{ color: "var(--app-gray-400)", fontSize: "0.82rem" }}>—</span>
            )
          }
        />
        <Table.Column
          key="tags"
          dataIndex="tags"
          title="Tag"
          render={(tags: string[] | null) =>
            tags?.length ? (
              <Space size={4} wrap>
                {tags.map((t) => (
                  <Tag key={t} color={tagColor(t)} style={{ margin: 0, fontSize: "0.75rem" }}>
                    {t}
                  </Tag>
                ))}
              </Space>
            ) : (
              <span style={{ color: "var(--app-gray-400)", fontSize: "0.82rem" }}>—</span>
            )
          }
        />
        <Table.Column
          key="weeks"
          title="Settimane"
          align="center"
          width={110}
          render={(_v, record: PrecompiledProgression) => (
            <span className="pp-week-badge">{record.weeks?.length ?? 0} sett.</span>
          )}
        />
        <Table.Column
          key="actions"
          title=""
          align="right"
          width={120}
          render={(_v, record: PrecompiledProgression) => (
            <Space size={4} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Copia progressione">
                <IconButton
                  onClick={handleCopyClick(record)}
                  disabled={copyingId === record.id}
                >
                  {copyingId === record.id ? (
                    <TailSpin width={14} height={14} color="currentColor" />
                  ) : (
                    <FontAwesomeIcon icon={faCopy} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Modifica">
                <IconButton onClick={handleEditClick(record)}>
                  <FontAwesomeIcon icon={faPen} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Elimina">
                <IconButton className="text-danger" onClick={handleDeleteClick(record)}>
                  <FontAwesomeIcon icon={faTrash} />
                </IconButton>
              </Tooltip>
            </Space>
          )}
        />
      </Table>
    );
  }, [items, error, filters, total, navigate, handleEditClick, handleDeleteClick, handleCopyClick, copyingId]);

  return (
    <Container fluid="md" className="mt-5">
      {/* header */}
      <Row className="align-items-start mb-1">
        <Col>
          <div className="pp-list-header">
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "var(--app-primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: "1rem",
                flexShrink: 0,
              }}
            >
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <div>
              <p className="pp-list-title">Progressioni Precompilate</p>
              <p className="pp-list-subtitle">
                Template di progressione riutilizzabili nelle programmazioni
              </p>
            </div>
          </div>
        </Col>
        <Col xs="auto" className="d-flex align-items-center gap-2 mt-1">
          <AppSearch onChange={handleSearch} />
          <button
            className="pp-new-btn"
            onClick={() => navigate("/progressioni-precompilate/nuovo")}
          >
            <span className="pp-new-btn-icon">
              <FontAwesomeIcon icon={faPlus} />
            </span>
            <span className="d-none d-sm-inline">Nuova Progressione</span>
          </button>
        </Col>
      </Row>

      {total !== undefined && (
        <p style={{ fontSize: "0.8rem", color: "var(--app-gray-500)", marginBottom: 0 }}>
          {total} {total === 1 ? "progressione" : "progressioni"} totali
        </p>
      )}

      <hr className="mt-2 mb-3" />

      <ConfigProvider renderEmpty={renderEmpty}>
        {items ? (
          renderTable()
        ) : (
          <div className="d-flex justify-content-center py-5 my-5">
            <TailSpin height={80} width={80} color="#9c1419" radius="1" />
          </div>
        )}
      </ConfigProvider>

      {/* Modale eliminazione */}
      <Modal show={deleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Elimina Progressione Precompilata
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler eliminare la progressione{" "}
            <strong>"{selectedItem?.label}"</strong>?
          </p>
          <Alert variant="warning" className="py-2">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2 small">Questa azione non può essere annullata.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteModalClose}>
            Annulla
          </Button>
          <Button variant="danger" size="sm" onClick={handleDelete}>
            {loading ? <TailSpin width={18} height={18} /> : "Elimina"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
